import { TestMarke } from "@/components/TestMarke";
import { meldeUngespeichert } from "@/lib/ungespeichertWarnung";
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { zurueckGehen } from "@/lib/zurueck";
import {
  ArrowLeft, User as UserIcon, MapPin, Phone, Mail, Edit, Plus,
  Folder, Image as ImageIcon, Upload, Trash2, X, Save, Sparkles,
  ChevronDown, ChevronUp, Calendar, Edit3, Globe, Wrench, FileText, Link as LinkIcon, Share2, Video, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Button, Card, Badge, Input, Textarea, Modal } from "@/components/common";
import { api } from "@/lib/api";
import { useF1Help } from "@/lib/useF1Help";
import { AufgabenPanel } from "@/components/AufgabenPanel";
import { TerminePanel } from "@/components/TerminePanel";
import KundenLinkDialog from "@/components/KundenLinkDialog";
import NewProjektDialog, { useTextvorlagen } from "@/components/NewProjektDialog";
import AuswahlFeld from "@/components/AuswahlFeld";
import { StatusAutomatikHinweis } from "@/components/StatusAutomatik";
import ProjektBild from "@/components/ProjektBild";
import MailHistoryModal from "@/components/MailHistoryModal";
import { MailLink } from "@/components/MailLink";
import { CustomerDocumentsPanel } from "@/components/CustomerDocumentsPanel";
import ProjektBelege from "@/components/ProjektBelege";
import { starteVorgang, beendeVorgang, aktion as ablaufAktion } from "@/lib/ablauf";
import PortalLinkDialog from "@/components/module_portal_wizard/PortalLinkDialog";
import { openInPopup, useBroadcast } from "@/lib/windowSync";

const STATUS_COLORS = {
  "Anfrage": "bg-blue-100 text-blue-700 border-blue-300",
  "In Bearbeitung": "bg-amber-100 text-amber-800 border-amber-300",
  "Abgeschlossen": "bg-emerald-100 text-emerald-700 border-emerald-300",
  "Archiv": "bg-gray-100 text-gray-600 border-gray-300",
};

/**
 * Werkbank: Kunde oben fest (sticky), Projekte unten scrollbar.
 * Routen-Eintritt:
 *   - aus Kunden-Modul: /module/projekte/werkbank/<kunde_id>
 *   - aus Projekte-Modul: Projekt-Karte -> springt auf werkbank/<kunde_id>?projekt=<id>
 */
const ProjektWerkbank = () => {
  useF1Help("hilfe_projekte");
  const { kunde_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [mailHistoryFor, setMailHistoryFor] = useState(null);
  // Zuletzt aufgeklapptes Projekt → im Mail-Fenster vorausgewählt (A-010 M1b)
  const [aktivesProjekt, setAktivesProjekt] = useState(searchParams.get("projekt_id") || "");
  const [showPortalLinkDialog, setShowPortalLinkDialog] = useState(false);
  const [portalLinkText, setPortalLinkText] = useState("");
  const [portalLinkResult, setPortalLinkResult] = useState("");
  const [portalLinkBusy, setPortalLinkBusy] = useState(false);
  const [hausverwaltungen, setHausverwaltungen] = useState([]);
  const [hvQuery, setHvQuery] = useState("");
  const [einsatzCounts, setEinsatzCounts] = useState({}); // { [projektId]: number } — Anzahl Einsaetze je Projekt
  // A-017: offene Eingänge dieses Kunden aus dem Kundenportal → Hinweis mit Sprung zur Liste
  const [eingaengeOffen, setEingaengeOffen] = useState(0);
  useEffect(() => {
    api.get("/kundenportal/eingaenge/zaehler", { params: { kunde_id } })
      .then((r) => setEingaengeOffen(r.data?.offen || 0)).catch(() => setEingaengeOffen(0));
  }, [kunde_id]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/module-projekte/werkbank/${kunde_id}`);
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
      if (!data) navigate("/module/projekte");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [kunde_id]);
  // Ablaufaufzeichnung (08.10.2026): Werkbank eines Kunden = ein Vorgang
  useEffect(() => {
    const vid = starteVorgang("Kunde bearbeiten (Werkbank)", { kunde_id });
    return () => beendeVorgang(vid, "ok");
  }, [kunde_id]);

  // Beim Öffnen der Werkbank nach oben scrollen, damit der Datensatz voll sichtbar ist
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [kunde_id]);
  const loadHausverwaltungen = async () => {
    try {
      const res = await api.get("/modules/kunden/data");
      setHausverwaltungen((res.data || []).filter(k => k.typ === "Hausverwaltung"));
    } catch { /* ignore */ }
  };
  useEffect(() => { loadHausverwaltungen(); }, []);
  const loadEinsatzCounts = async () => {
    try {
      const res = await api.get(`/einsaetze?kunde_id=${kunde_id}`);
      const counts = {};
      (res.data || []).forEach((e) => {
        if (e.projekt_id) counts[e.projekt_id] = (counts[e.projekt_id] || 0) + 1;
      });
      setEinsatzCounts(counts);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadEinsatzCounts(); }, [kunde_id]);

  if (loading && !data) return <div className="p-8 text-center text-muted-foreground">Lade…</div>;
  if (!data) return null;

  const { kunde, projekte, stats } = data;
  const kundeName = `${kunde.vorname || ""} ${kunde.nachname || ""}`.trim() || kunde.name || "(ohne Name)";

  return (
    <div data-testid="projekt-werkbank-page" className="pb-12">
      {/* === Sticky Kunden-Block === */}
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b shadow-sm -mx-4 lg:-mx-8 px-4 lg:px-8 pt-2 pb-4 mb-4 xl:pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <Button variant="outline" size="sm" onClick={() => zurueckGehen(navigate, location, "/module/projekte")} data-testid="btn-back-werkbank">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0 flex-1">
              <div className="xl:flex xl:items-center xl:gap-3">
              <span className="inline-block px-3 py-1.5 rounded-sm text-sm font-bold bg-blue-600 text-white mb-1.5 xl:mb-0 xl:px-2 xl:py-0.5 xl:text-xs xl:whitespace-nowrap">Projekte-Werkbank</span>
              <div className="flex items-center gap-2 flex-wrap">
                <UserIcon className="w-5 h-5 text-primary flex-shrink-0" />
                <h1 className="text-xl lg:text-2xl font-bold truncate">{kundeName}</h1>
                {kunde.testkunde === true && <TestMarke />}
                {kunde.kontakt_status && (
                  <Badge variant="outline" className="text-xs">{kunde.kontakt_status}</Badge>
                )}
                {kunde.firma && <Badge variant="outline" className="text-xs">{kunde.firma}</Badge>}
              </div>
              </div>
              <div className="text-sm text-muted-foreground mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                {(kunde.address || kunde.strasse || kunde.ort) && (
                  
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(kunde.address || `${kunde.strasse || ""} ${kunde.hausnummer || ""}, ${kunde.plz || ""} ${kunde.ort || ""}`.replace(/, *$/, "").trim())}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 hover:underline"
                  >
                    <MapPin className="w-3.5 h-3.5" />
                    {kunde.address || `${kunde.strasse || ""} ${kunde.hausnummer || ""}, ${kunde.plz || ""} ${kunde.ort || ""}`.replace(/, *$/, "").trim()}
                  </a>
                )}
                {(kunde.phone || kunde.mobile) && (
                  <a href={`tel:${kunde.phone || kunde.mobile}`} className="flex items-center gap-1 hover:text-primary">
                    <Phone className="w-3.5 h-3.5" /> {kunde.phone || kunde.mobile}
                  </a>
                )}
                {kunde.email && (
                  <MailLink email={kunde.email} showIcon className="max-lg:truncate max-lg:max-w-full" />
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col lg:flex-row lg:flex-wrap lg:justify-end items-stretch lg:items-center gap-2 w-full lg:w-auto lg:flex-shrink-0 xl:max-w-[60%] [&>button]:w-full lg:[&>button]:w-auto">
            <Button variant="outline" size="sm" onClick={() => navigate(`/module/kunden?edit=${kunde.id}`)} data-testid="btn-kundenuebersicht">
              <UserIcon className="w-4 h-4" /> Kundenübersicht
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(`/module/kundenportal?kunde_id=${kunde.id}`)} data-testid="btn-zum-kundenportal">
              <Share2 className="w-4 h-4" /> Zum Kundenportal
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(`/module/kunden?edit=${kunde.id}&returnTo=${encodeURIComponent(`/module/projekte/werkbank/${kunde.id}`)}`)} data-testid="btn-edit-kunde">
              <Edit className="w-4 h-4" /> Kunde bearbeiten
            </Button>
          {(kunde.email || (kunde.mail_adressen || []).some((z) => z.adresse)) && (
            <Button variant="outline" size="sm"
              onClick={() => setMailHistoryFor({
                kundeId: kunde.id,
                email: kunde.email || (kunde.mail_adressen || []).find((z) => z.adresse).adresse,
                name: `${kunde.vorname || ""} ${kunde.nachname || ""}`.trim() || kunde.name || kunde.email,
              })}
              className="mt-1 lg:mt-0" // am Handy wie bisher 12 px Abstand zur Gruppe darüber
              data-testid="btn-werkbank-mail-history"
              title="Alle Mails von/an diesen Kunden aus IMAP anzeigen"
            >
              <Mail className="w-4 h-4" /> Mailverlauf
            </Button>
          )}
          {/* Altes Portal ausgeblendet (13.07.2026) — nicht gelöscht.
              Richtiger Weg ist jetzt "Kundenportal" links in der Navigation (module_portal_wizard). */}
          {false && (
          <button
            onClick={async () => {
              try {
                const res = await api.get(`/portals/for-customer/${kunde_id}`);
                if (res.data?.exists && res.data?.portal?.id) {
                  navigate(`/portals?portal=${res.data.portal.id}`);
                } else {
                  if (!kunde.email) { toast.error("Kunde hat keine E-Mail – erst ergänzen"); return; }
                  if (!window.confirm(`Neues Kundenportal für ${kunde.vorname || ""} ${kunde.nachname || ""}${kunde.firma ? ` (${kunde.firma})` : ""} anlegen?`)) return;
                  const created = await api.post(`/portals/from-customer/${kunde_id}`, {});
                  const newId = created.data?.id;
                  toast.success("Portal erstellt");
                  navigate(newId ? `/portals?portal=${newId}` : `/portals`);
                }
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Fehler");
              }
            }}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-sm bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-colors"
            data-testid="btn-werkbank-portal"
          >
            <Globe className="w-4 h-4" /> Kundenportal öffnen / anlegen
          </button>
          )}
          <Button variant="outline" size="sm"
            onClick={() => { setShowPortalLinkDialog(true); setPortalLinkText(""); setPortalLinkResult(""); }}
            className={kunde.email ? "" : "mt-1 lg:mt-0"}
            data-testid="btn-werkbank-portal-link-erstellen"
            title="Einmaligen Portal-Link für den Kunden erzeugen"
          >
            <LinkIcon className="w-4 h-4" />
            🔗 Portal-Link erstellen
          </Button>
          </div>
        </div>
      </div>

      {/* Ab 1280 px zweispaltig: links Dokumente & Vorgänge, rechts Projekte (Vorschlag BUT-0001) */}
      <div className="xl:grid xl:grid-cols-[minmax(0,11fr)_minmax(0,9fr)] 2xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] xl:gap-6 xl:items-start">
      {/* Dokumente & Vorgaenge */}
      <div className="mb-4 xl:mb-0">
        <CustomerDocumentsPanel customerId={kunde_id} kompakt offenesProjekt={projekte.find((p) => p.id === aktivesProjekt) || null} />
      </div>
      <section>

      {/* === Projekte === */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Folder className="w-5 h-5 text-emerald-600" />
          Projekte
          <Badge variant="outline" className="text-xs">{stats.projekte_total} gesamt · {stats.projekte_aktiv} aktiv</Badge>
        </h2>
        <Button
          onClick={() => setShowNew(true)}
          size="sm"
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="btn-new-projekt"
        >
          <Plus className="w-4 h-4" /> Neues Projekt
        </Button>
      </div>

      {eingaengeOffen > 0 && (
        <button type="button" onClick={() => navigate(`/module/mail-inbox?reiter=eingaenge&kunde_id=${kunde_id}`)}
          className="w-full flex items-center gap-2 rounded-md border border-blue-300 bg-blue-50 dark:bg-blue-950/30 px-3 py-2.5 text-left text-sm text-blue-900 dark:text-blue-200 hover:bg-blue-100"
          data-testid="werkbank-eingaenge-hinweis">
          <ImageIcon className="w-4 h-4 shrink-0" />
          <span className="font-medium">{eingaengeOffen === 1 ? "1 neuer Eingang" : `${eingaengeOffen} neue Eingänge`} vom Kunden</span>
          <span className="text-xs">(Fotos/Nachricht aus dem Kundenportal)</span>
          <span className="ml-auto text-xs underline whitespace-nowrap">ansehen →</span>
        </button>
      )}

      {projekte.length === 0 ? (
        <Card className="p-8 text-center" data-testid="empty-state-werkbank">
          <Folder className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
          <div className="text-lg font-semibold">Noch keine Projekte für {kundeName}</div>
          <div className="text-sm text-muted-foreground mt-1">
            Klicke auf „+ Neues Projekt" — Adresse, Anliegen und Kategorie werden automatisch
            aus den Kundendaten vorgeschlagen.
          </div>
        </Card>
      ) : (
        <div className="space-y-4 xl:space-y-2">
          {projekte.map(p => (
            <ProjektKarte key={p.id} projekt={p} kundeId={kunde_id} kunde={kunde} onChanged={load} onEinsatz={(projektId, projektTitel) => navigate(`/einsaetze?projekt_id=${projektId}&kunde_id=${kunde_id}&projekt_titel=${encodeURIComponent(projektTitel)}`)} hausverwaltungen={hausverwaltungen} reloadHausverwaltungen={loadHausverwaltungen} einsatzCount={einsatzCounts[p.id] ?? null} autoExpand={p.id === searchParams.get("projekt_id")} startTab={p.id === searchParams.get("projekt_id") ? searchParams.get("tab") || "" : ""} onOeffnen={setAktivesProjekt} onSchliessen={(pid) => setAktivesProjekt((a) => (a === pid ? "" : a))} />
          ))}
        </div>
      )}
      </section>
      </div>

      {showNew && (
        <NewProjektDialog
          kundeId={kunde_id}
          kunde={kunde}
          isFirstProjekt={projekte.length === 0}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(); }}
        />
      )}

      <MailHistoryModal
        isOpen={!!mailHistoryFor}
        onClose={() => setMailHistoryFor(null)}
        email={mailHistoryFor?.email || ""}
        kundeName={mailHistoryFor?.name || ""}
        kundeId={mailHistoryFor?.kundeId || ""}
        projekte={projekte || []}
        projektId={aktivesProjekt || searchParams.get("projekt_id") || ""}
      />

      <PortalLinkDialog
        kunde={showPortalLinkDialog ? kunde : null}
        projekt={showPortalLinkDialog ? data : null}
        onClose={() => setShowPortalLinkDialog(false)}
      />
    </div>
  );
};

// ==================== Projekt-Karte (inline editierbar) ====================
const ProjektKarte = ({ projekt, kundeId, kunde, onChanged, onEinsatz, hausverwaltungen, reloadHausverwaltungen, einsatzCount, autoExpand, startTab = "", onOeffnen, onSchliessen }) => {
  const [expanded, setExpanded] = useState(!!autoExpand);
  // A-017: Sprung aus „Vom Kunden eingegangen“ öffnet gleich Bilder/Termine/Aufgaben
  const [activeTab, setActiveTab] = useState(["aufgaben", "termine", "bilder"].includes(startTab) ? startTab : "details");
  const [data, setData] = useState(projekt);
  const [saving, setSaving] = useState(false);
  const [uploadKategorie, setUploadKategorie] = useState("schaden");
  const [uploading, setUploading] = useState(false);
  const [isDraggingBilder, setIsDraggingBilder] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [hvQuery, setHvQuery] = useState("");
  const [kontakte, setKontakte] = useState([]); // A-020 P4: „Weitere Kontakte“ des Kunden (Ansprechpartner vor Ort)
  useEffect(() => {
    let lebt = true;
    if (!kundeId) return undefined;
    api.get(`/module-projekte/kontakte-des-kunden/${kundeId}`).then((r) => { if (lebt) setKontakte(r.data || []); }).catch(() => {});
    return () => { lebt = false; };
  }, [kundeId, projekt?.updated_at]);
  const [tabStats, setTabStats] = useState({ aufgaben: null, termine: null });
  const fileInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const navigate = useNavigate();

  const statusOptions = useTextvorlagen("projekt_status");
  const bildKatOptions = useTextvorlagen("projekt_bild_kategorie");
  const statusList = statusOptions.map(s => s.title).filter(Boolean);
  const bildKatList = bildKatOptions.map(s => s.title).filter(Boolean);

  // Wenn der Outer-projekt-prop sich aendert (nach load): synchronisieren
  // BUT-0038: Felder, die der Benutzer in der Karte geaendert hat (noch nicht gespeichert), bleiben erhalten
  const geaendertRef = useRef(new Set());
  useEffect(() => { const k = `projekt-${projekt.id}`; return () => meldeUngespeichert(k, false); }, [projekt.id]);
  useEffect(() => {
    setData((alt) => {
      const behalten = {};
      geaendertRef.current.forEach((f) => { if (alt && f in alt) behalten[f] = alt[f]; });
      return { ...projekt, ...behalten };
    });
  }, [projekt]);

  // Tab-Stats: laden beim Expand und beim Tab-Wechsel (refetch sorgt fuer frische Counts nach Mutation)
  useEffect(() => {
    if (!expanded) return;
    let cancelled = false;
    const loadStats = async () => {
      try {
        const [a, t] = await Promise.all([
          api.get("/module-aufgaben/stats/uebersicht", { params: { projekt_id: data.id } }).then(r => r.data).catch(() => null),
          api.get("/module-termine/stats/uebersicht", { params: { projekt_id: data.id } }).then(r => r.data).catch(() => null),
        ]);
        if (!cancelled) setTabStats({ aufgaben: a, termine: t });
      } catch { /* ignore */ }
    };
    loadStats();
    return () => { cancelled = true; };
  }, [expanded, activeTab, data.id]);

  const update = (field, value) => { geaendertRef.current.add(field); meldeUngespeichert(`projekt-${data.id}`, true); setData(d => ({ ...d, [field]: value })); };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/module-projekte/${data.id}`, {
        titel: data.titel,
        beschreibung: data.beschreibung,
        kategorie: data.kategorie,
        adresse: data.adresse,
        hausverwaltung_id: data.hausverwaltung_id,
        ansprechpartner_id: data.ansprechpartner_id || "",
        status: data.status,
        notizen: data.notizen,
        vor_ort_notizen: data.vor_ort_notizen,
      });
      geaendertRef.current.clear();
      meldeUngespeichert(`projekt-${data.id}`, false);
      setData(res.data);
      toast.success("Projekt gespeichert");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleInaktiv = async () => {
    const aktivieren = data.status === "Archiv";
    const neuerStatus = aktivieren ? "Anfrage" : "Archiv";
    const frage = aktivieren
      ? `Projekt "${data.titel}" wieder aktivieren?`
      : `Projekt "${data.titel}" inaktiv setzen?`;

    if (!window.confirm(frage)) return;
    setSaving(true);
    try {
      const res = await api.put(`/module-projekte/${data.id}`, {
        titel: data.titel,
        beschreibung: data.beschreibung,
        kategorie: data.kategorie,
        adresse: data.adresse,
        hausverwaltung_id: data.hausverwaltung_id,
        status: neuerStatus,
        notizen: data.notizen,
        vor_ort_notizen: data.vor_ort_notizen,
      });
      setData(res.data);
      toast.success(aktivieren ? "Projekt wieder aktiviert" : "Projekt inaktiv gesetzt");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Projekt "${data.titel}" wirklich löschen?`)) return;
    try {
      await api.delete(`/module-projekte/${data.id}`);
      toast.success("Projekt gelöscht");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const isVideo = file.type.startsWith("video/");
        const endpoint = isVideo ? "videos" : "bilder";
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/module-projekte/${data.id}/${endpoint}?kategorie=${uploadKategorie}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${files.length} Datei(en) hochgeladen`);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (videoInputRef.current) videoInputRef.current.value = "";
    }
  };
  const handleFileChange = async (e) => {
    uploadFiles(e.target.files);
  };
  const handleBilderDrop = async (e) => {
    e.preventDefault();
    setIsDraggingBilder(false);
    const files = Array.from(e.dataTransfer.files || []).filter(f => f.type.startsWith("image/") || f.type.startsWith("video/"));
    if (files.length === 0) return;
    uploadFiles(files);
  };

  const deleteBild = async (bildId) => {
    if (!window.confirm("Bild wirklich löschen?")) return;
    try {
      await api.delete(`/module-projekte/${data.id}/bilder/${bildId}`);
      toast.success("Bild gelöscht");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const handlePdfChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadingPdf(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/module-projekte/${data.id}/pdfs`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${files.length} PDF(s) hochgeladen`);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setUploadingPdf(false);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  };

  const deletePdf = async (pdfId) => {
    try {
      await api.delete(`/module-projekte/${data.id}/pdfs/${pdfId}`);
      toast.success("PDF gelöscht");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const bilder = data.bilder || [];
  const pdfs = data.pdfs || [];

  return (
    <Card className="overflow-hidden" data-testid={`projekt-card-${data.id}`}>
      {/* Header (immer sichtbar) */}
      <div
        className="p-4 xl:px-3 xl:py-2 cursor-pointer hover:bg-muted/30"
        onClick={() => { if (!expanded) { ablaufAktion("Projekt öffnen", { projekt_id: data.id, kunde_id: kundeId }); onOeffnen?.(data.id); } else onSchliessen?.(data.id); setExpanded(!expanded); }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg xl:text-base font-semibold">{data.titel}</h3>
              <Badge className={`text-xs border ${STATUS_COLORS[data.status] || ""}`}>{data.status}</Badge>
              {data.kategorie && <Badge variant="outline" className="text-xs">{data.kategorie}</Badge>}
              <button
                onClick={(ev) => { ev.stopPropagation(); onEinsatz?.(data.id, data.titel); }}
                className="inline-flex items-center gap-1 border border-orange-300 text-orange-700 hover:bg-orange-50 rounded-sm px-2 py-0.5 text-xs"
                data-testid={`btn-projekt-einsatz-${data.id}`}
                title="Neuen Einsatz für dieses Projekt anlegen"
              >
                <Wrench className="w-3.5 h-3.5" /> Einsatz
                {einsatzCount > 0 && (
                  <span className="ml-1 bg-orange-200 text-orange-800 rounded-full px-1.5">{einsatzCount}</span>
                )}
              </button>
              {(data.status === "Abgeschlossen" || data.status === "Archiv") && (
                <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-0.5" title="Dieses Projekt ist archiviert. Bearbeitung ist trotzdem moeglich.">
                  ⚠ Archiviert – wird trotzdem bearbeitet
                </span>
              )}
              {bilder.length > 0 && (
                <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                  <ImageIcon className="w-3 h-3" /> {bilder.length}
                </span>
              )}
              {data.aus_anfrage && <Badge className="text-xs bg-emerald-100 text-emerald-700 border-emerald-300">aus Anfrage</Badge>}
              <span className="hidden xl:inline-flex items-center gap-1 ml-auto text-xs text-muted-foreground"><Calendar className="w-3 h-3" />{(data.created_at || "").slice(0, 10)}</span>
            </div>
            {data.beschreibung && !expanded && (
              <p className="text-sm text-slate-700 mt-1 xl:mt-0.5 line-clamp-1">{data.beschreibung}</p>
            )}
            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2 xl:hidden">
              <Calendar className="w-3 h-3" />{(data.created_at || "").slice(0, 10)}
            </div>
          </div>
          {expanded ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
        </div>
      </div>

      {/* A-015b: Kopf des geöffneten Projekts – neue Belege für dieses Projekt + Vorgangskette (statt Reiter „Belege“) */}
      {expanded && (
        <div className="border-t bg-background px-4 py-3 xl:px-3 xl:py-2.5">
          <ProjektBelege projektId={data.id} kundeId={kundeId} kunde={kunde} />
        </div>
      )}

      {/* Detail (ausgeklappt) */}
      {expanded && (
        <div className="border-t bg-muted/10 p-4 space-y-4">
          {/* Tab-Strip */}
          <div className="flex max-lg:flex-wrap gap-1 border-b" data-testid={`tabs-projekt-${data.id}`}>
            {(() => {
              const aufgOpen = (tabStats.aufgaben?.offen || 0) + (tabStats.aufgaben?.in_arbeit || 0);
              const aufgTotal = tabStats.aufgaben?.gesamt ?? null;
              const termOpen = (tabStats.termine?.wartet_auf_go || 0);
              const termTotal = tabStats.termine?.gesamt ?? null;
              const tabs = [
                { id: "details", label: "Details" },
                { id: "aufgaben", label: "Aufgaben", count: aufgTotal, hasOffen: aufgOpen > 0 },
                { id: "termine", label: "Termine", count: termTotal, hasOffen: termOpen > 0 },
                { id: "bilder", label: "Bilder", count: bilder.length },
                { id: "pdfs", label: "PDF", count: pdfs.length },
              ];
              return tabs.map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActiveTab(t.id)}
                  data-testid={`tab-${t.id}-${data.id}`}
                  className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-1 ${activeTab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                >
                  <span>{t.label}</span>
                  {t.count != null && t.count > 0 && (
                    <span className="text-xs font-semibold bg-blue-600 text-white rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center">{t.count}</span>
                  )}
                  {t.hasOffen && (
                    <span
                      className="inline-block w-2 h-2 rounded-full bg-orange-500"
                      title="Offene Eintraege"
                      data-testid={`tab-${t.id}-${data.id}-dot`}
                    />
                  )}
                </button>
              ));
            })()}
          </div>

          {/* Tab: Details */}
          {activeTab === "details" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Titel *</label>
                <Input value={data.titel || ""} onChange={(e) => update("titel", e.target.value)} data-testid={`input-titel-${data.id}`} />
              </div>
              {/* A-012: Status als Auswahlliste; darunter Hinweis der Status-Automatik, Verlauf, Link „Regeln“ */}
              <AuswahlFeld docType="projekt_status" label="Status" value={data.status || ""} ersatz={statusList}
                onChange={(s) => update("status", s)} stichwoerter={false} testId={`projekt-status-${data.id}`}
                hinweis={<StatusAutomatikHinweis art="projekt" id={data.id} info={data.status_automatik} neuLaden={data.updated_at} />} />
              {/* A-011: Kategorie aus der führenden Liste kunden_kategorie (wie Anfrage/Kunde/Einsatz) */}
              <AuswahlFeld docType="kunden_kategorie" label="Kategorie" value={data.kategorie || ""}
                onChange={(k) => update("kategorie", k)} leer="— keine —" testId={`projekt-kategorie-${data.id}`} />
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Baustelle (Adresse, wo gearbeitet wird)</label>
                <Input value={data.adresse || ""} onChange={(e) => update("adresse", e.target.value)} data-testid={`projekt-baustelle-${data.id}`} />
                <p className="text-[11px] text-muted-foreground mt-0.5">Leer = Adresse des Kunden. Die Rechnung geht an den Kunden.</p>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Ansprechpartner vor Ort</label>
                <select value={data.ansprechpartner_id || ""} onChange={(e) => update("ansprechpartner_id", e.target.value)}
                  className="h-9 w-full rounded-sm border border-input bg-background px-2 text-sm" data-testid={`projekt-ansprechpartner-${data.id}`}>
                  <option value="">Kunde selbst</option>
                  {kontakte.map((k) => (
                    <option key={k.id} value={k.id}>{[k.rolle, k.name].filter(Boolean).join(" ") || k.telefon || "Kontakt"}{k.telefon ? ` · ${k.telefon}` : ""}</option>
                  ))}
                </select>
                {kontakte.length === 0 && <p className="text-[11px] text-muted-foreground mt-0.5">Weitere Kontakte legen Sie beim Kunden an („Kunde bearbeiten“ → „+ Kontakt“).</p>}
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Hausverwaltung</label>
                {data.hausverwaltung_id ? (
                  (() => {
                    const hv = hausverwaltungen.find(h => h.id === data.hausverwaltung_id);
                    const hvName = hv ? (hv.name || hv.firma || `${hv.vorname || ""} ${hv.nachname || ""}`.trim()) : "(wird geladen…)";
                    return (
                      <div className="flex items-center gap-2 p-2 border rounded bg-muted/40">
                        <button type="button" className="flex-1 text-left text-sm hover:underline" onClick={() => navigate(`/module/kunden?edit=${data.hausverwaltung_id}&returnTo=${encodeURIComponent(`/module/projekte/werkbank/${kunde.id}?projekt=${data.id}`)}`)}>
                          {hvName}
                        </button>
                        <button type="button" className="text-xs text-primary hover:underline" onClick={() => update("hausverwaltung_id", null)}>Ändern</button>
                      </div>
                    );
                  })()
                ) : (
                  <>
                    <Input value={hvQuery} onChange={(e) => setHvQuery(e.target.value)} placeholder="Hausverwaltung suchen (Name, Telefon, Mobil)…" />
                    {hvQuery && (() => {
                      const qDigits = hvQuery.replace(/\s|\/|-/g, "");
                      const hvMatches = hausverwaltungen.filter(h => {
                        const name = (h.name || h.firma || `${h.vorname || ""} ${h.nachname || ""}`).toLowerCase();
                        if (name.includes(hvQuery.toLowerCase())) return true;
                        if (qDigits.length < 5) return false;
                        const phoneDigits = (h.phone || "").replace(/\s|\/|-/g, "");
                        const mobileDigits = (h.mobile || "").replace(/\s|\/|-/g, "");
                        return phoneDigits.includes(qDigits) || mobileDigits.includes(qDigits);
                      }).slice(0, 12);
                      if (hvMatches.length === 0) {
                        return (
                          <div className="mt-1 border rounded p-2 text-sm">
                            <button
                              type="button"
                              className="text-primary hover:underline"
                              onClick={() => navigate(`/module/kunden?new=1&returnTo=${encodeURIComponent(`/module/projekte/werkbank/${kunde.id}?projekt=${data.id}`)}`)}
                            >
                              Nicht gefunden – Kundendatei öffnen?
                            </button>
                          </div>
                        );
                      }
                      return (
                        <div className="mt-1 border rounded max-h-52 overflow-y-auto">
                          {hvMatches.map(h => (
                            <button key={h.id} type="button" onClick={() => { update("hausverwaltung_id", h.id); setHvQuery(""); }} className="w-full text-left px-3 py-2 hover:bg-muted text-sm border-b last:border-0">
                              {h.name || h.firma || `${h.vorname || ""} ${h.nachname || ""}`.trim()}
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-muted-foreground block mb-1">Beschreibung</label>
                <Textarea value={data.beschreibung || ""} onChange={(e) => update("beschreibung", e.target.value)} rows={3} />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-muted-foreground block mb-1">Notizen (intern)</label>
                <Textarea value={data.notizen || ""} onChange={(e) => update("notizen", e.target.value)} rows={2} />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-muted-foreground block mb-1">Vor-Ort-Notizen (Mitarbeiter-Link, per Sprache)</label>
                <Textarea value={data.vor_ort_notizen || ""} onChange={(e) => update("vor_ort_notizen", e.target.value)} rows={4} />
              </div>
            </div>
          )}


          {/* Tab: Aufgaben */}
          {activeTab === "aufgaben" && (
            <AufgabenPanel
              kunde_id={kundeId}
              projekt_id={data.id}
              title="Aufgaben dieses Projekts"
              defaultCollapsed={false}
              compact
            />
          )}

          {/* Tab: Termine */}
          {activeTab === "termine" && (
            <TerminePanel
              kunde_id={kundeId}
              projekt_id={data.id}
              title="Termine dieses Projekts"
              defaultCollapsed={false}
              compact
            />
          )}

          {/* Tab: Bilder */}
          {activeTab === "bilder" && (
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDraggingBilder(true); }}
              onDragEnter={(e) => { e.preventDefault(); setIsDraggingBilder(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDraggingBilder(false); }}
              onDrop={handleBilderDrop}
              className={`rounded transition-colors ${isDraggingBilder ? "bg-muted/40 ring-2 ring-primary ring-inset" : ""}`}
            >
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <h4 className="text-sm font-semibold flex items-center gap-1">
                  <ImageIcon className="w-4 h-4" /> Bilder ({bilder.length})
                </h4>
                <div className="flex items-center gap-1">
                  <select value={uploadKategorie} onChange={(e) => setUploadKategorie(e.target.value)} className="border rounded px-2 py-1 text-xs">
                    {bildKatList.map(k => <option key={k} value={k}>{k}</option>)}
                  </select>
                  <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid={`btn-upload-${data.id}`}>
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {uploading ? "Lade…" : "Hochladen"}
                  </Button>
                  <input ref={fileInputRef} type="file" multiple accept="image/*" capture="environment" onChange={handleFileChange} className="hidden" />
                  <Button size="sm" variant="outline" onClick={() => videoInputRef.current?.click()} disabled={uploading} data-testid={`btn-upload-video-${data.id}`}>
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Video className="w-3.5 h-3.5" />} {uploading ? "Lade…" : "Video hochladen"}
                  </Button>
                  <input ref={videoInputRef} type="file" multiple accept="video/*" onChange={handleFileChange} className="hidden" />
                </div>
              </div>
              {bilder.length === 0 ? (
                <div className="text-xs text-muted-foreground border-2 border-dashed rounded p-4 text-center">
                  {isDraggingBilder ? "Bilder hier loslassen…" : "Keine Bilder. Wähle Kategorie und lade Bilder hoch, oder ziehe Bilder hierher."}
                </div>
              ) : (
                <BilderGrid bilder={bilder} onDelete={deleteBild} projektId={data.id} />
              )}
            </div>
          )}
          {activeTab === "pdfs" && (
            <div>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <h4 className="text-sm font-semibold flex items-center gap-1">
                  <FileText className="w-4 h-4" /> PDF-Dokumente ({pdfs.length})
                </h4>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" onClick={() => pdfInputRef.current?.click()} disabled={uploadingPdf} data-testid={`btn-upload-pdf-${data.id}`}>
                    <Upload className="w-3.5 h-3.5" /> {uploadingPdf ? "Lade…" : "PDF hochladen"}
                  </Button>
                  <input ref={pdfInputRef} type="file" multiple accept="application/pdf" onChange={handlePdfChange} className="hidden" />
                </div>
              </div>
              {pdfs.length === 0 ? (
                <div className="text-xs text-muted-foreground border-2 border-dashed rounded p-4 text-center">
                  Keine PDFs. Lade Dokumente hoch.
                </div>
              ) : (
                <div className="space-y-1">
                  {pdfs.map(pdf => (
                    <div key={pdf.id} className="flex items-center justify-between text-xs border rounded px-2 py-1.5 bg-muted/20">
                      <button
                        onClick={async () => {
                          try {
                            const r = await api.get(`/module-projekte/files/${pdf.url}`, { responseType: "blob" });
                            const objUrl = URL.createObjectURL(r.data);
                            window.open(objUrl, "_blank");
                          } catch (err) {
                            toast.error("PDF konnte nicht geladen werden");
                          }
                        }}
                        className="flex items-center gap-1.5 text-blue-700 hover:underline truncate text-left"
                      >
                        <FileText className="w-3.5 h-3.5 flex-shrink-0" /> {pdf.filename}
                      </button>
                      <button onClick={() => deletePdf(pdf.id)} className="text-red-600 hover:bg-red-50 rounded p-1 flex-shrink-0" title="PDF löschen">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="relative w-full min-w-0 mt-4 lg:fixed lg:bottom-3 lg:left-64 lg:right-3 lg:w-auto z-30 bg-white/95 backdrop-blur p-2 border-t shadow-sm space-y-2 lg:flex lg:items-center lg:justify-between lg:space-y-0 lg:gap-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 [&>button]:w-full lg:flex lg:flex-wrap lg:[&>button]:w-auto">
              <Button size="sm" onClick={save} disabled={saving} data-testid={`btn-save-${data.id}`}>
                <Save className="w-4 h-4 mr-1" /> {saving ? "Speichere…" : "Speichern"}
              </Button>
              <Button variant="outline" size="sm" onClick={() => { ablaufAktion("Angebot erstellen", { projekt_id: data.id, kunde_id: kundeId }); navigate(`/quotes/new?customer=${kundeId}&projekt_id=${data.id}`); }} data-testid={`btn-projekt-angebot-${data.id}`}>
                <FileText className="w-4 h-4 mr-1" /> Angebot
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowLinkDialog(true)} data-testid={`btn-projekt-link-${data.id}`}>
                <Mail className="w-4 h-4 mr-1" /> Mitarbeiter-Link
              </Button>
              <Button variant="outline" size="sm" onClick={handleInaktiv} disabled={saving} data-testid={`btn-inaktiv-${data.id}`}>
                {data.status === "Archiv" ? "Wieder aktivieren" : "Inaktiv setzen"}
              </Button>
            </div>
            <div className="flex justify-center border-t pt-2 lg:border-t-0 lg:pt-0">
              <Button variant="outline" size="sm" onClick={handleDelete} className="text-red-600 hover:bg-red-50" data-testid={`btn-delete-${data.id}`}>
                <Trash2 className="w-4 h-4 mr-1" /> Löschen
              </Button>
            </div>
          </div>
        </div>
      )}

      <KundenLinkDialog
        isOpen={showLinkDialog}
        onClose={() => setShowLinkDialog(false)}
        kunde={kunde ? { ...kunde, id: kundeId } : { id: kundeId }}
        projekt={{ id: data.id, titel: data.titel }}
      />
    </Card>
  );
};

const BilderGrid = ({ bilder, onDelete, projektId }) => {
  const bildKats = useTextvorlagen("projekt_bild_kategorie");
  const kategorien = bildKats.map(b => b.title).filter(Boolean);
  // Bilder ohne bekannte Kategorie unter "sonstiges" gruppieren
  const known = new Set(kategorien);
  const groups = kategorien.reduce((acc, kat) => {
    acc[kat] = bilder.filter(b => b.kategorie === kat);
    return acc;
  }, {});
  const orphan = bilder.filter(b => !known.has(b.kategorie));
  if (orphan.length) {
    const fallback = kategorien.includes("sonstiges") ? "sonstiges" : kategorien[0] || "sonstiges";
    groups[fallback] = [...(groups[fallback] || []), ...orphan];
  }
  return (
    <div className="space-y-3">
      {kategorien.map(kat => (groups[kat] || []).length > 0 && (
        <div key={kat}>
          <div className="text-xs font-medium text-slate-600 capitalize mb-1">{kat} ({groups[kat].length})</div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {groups[kat].map(b => (
              <ProjektBild key={b.id} bild={b} onDelete={onDelete} projektId={projektId} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};


export default ProjektWerkbank;
export { ProjektWerkbank };
